import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { RadioGroup } from "../../../../ui/components/RadioGroup";
import { FeatherHelpCircle, FeatherUpload, FeatherDownload, FeatherFileText, FeatherCheckCircle, FeatherXCircle, FeatherChevronDown, FeatherChevronUp, FeatherRefreshCw, FeatherTrash2, FeatherArrowLeft, FeatherAlertTriangle } from "@subframe/core";
import { uploadMapping, uploadManualMapping, uploadTrialBalance, UploadMappingResponse, UploadTrialBalanceResponse, UploadError, fetchGLMappingStats, GLMappingStats, GLMappingStatsResponse, downloadTrialBalanceData, truncateAll, TruncateAllResponse, getDatabookViewSetting, saveDatabookViewSetting, type DatabookViewScale, getStoredCompanyId } from "../../data/api/trialBalanceApi";
import { syncZohoAccounts, syncZohoTransactions, syncZohoTransactionsDebug, truncateZohoAccounts, truncateZohoTransactions, getZohoSyncStatus, syncZohoGlMappings, truncateZohoGlMappings, ZohoSyncAccountsResponse, ZohoSyncTransactionsResponse, ZohoSyncStatusResponse, ZohoSyncGlMappingsResponse, syncZohoTrialBalance, truncateZohoTrialBalance, downloadZohoTrialBalanceMonthWise, ZohoSyncTrialBalanceResponse, ZohoSyncTransactionsDebugResponse } from "../../data/api/zohoSyncApi";
import { API_BASE_URL } from "../../../../config/api";
import { getNumberFormatSetting, saveNumberFormatSetting } from "../../../../services/numberFormat";
import type { NumberFormatScale } from "../../../../shared/utils/formatters";

export const TrialBalanceUploadPage: React.FC = () => {
  const { company } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mappingFileInputRef = useRef<HTMLInputElement>(null);
  const manualMappingFileInputRef = useRef<HTMLInputElement>(null);
  const rulesFileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedMappingFile, setSelectedMappingFile] = useState<File | null>(null);
  const [selectedManualMappingFile, setSelectedManualMappingFile] = useState<File | null>(null);
  const [selectedRulesFile, setSelectedRulesFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingMapping, setIsUploadingMapping] = useState(false);
  const [isUploadingManualMapping, setIsUploadingManualMapping] = useState(false);
  const [isUploadingRules, setIsUploadingRules] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingExisting, setIsDownloadingExisting] = useState(false);
  const [isDownloadingManualExisting, setIsDownloadingManualExisting] = useState(false);
  const [isDownloadingTrialBalance, setIsDownloadingTrialBalance] = useState(false);
  const [isDownloadingRules, setIsDownloadingRules] = useState(false);
  const [isDownloadingTbData, setIsDownloadingTbData] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [uploadResult, setUploadResult] = useState<UploadMappingResponse | null>(null);
  const [manualMappingUploadResult, setManualMappingUploadResult] = useState<UploadMappingResponse | null>(null);
  const [trialBalanceResult, setTrialBalanceResult] = useState<UploadTrialBalanceResponse | null>(null);
  const [rulesUploadResult, setRulesUploadResult] = useState<{success: boolean, message: string} | null>(null);
  const [rulesSheetLastModified, setRulesSheetLastModified] = useState<string | null>(null);
  const [rulesSheetInfoLoading, setRulesSheetInfoLoading] = useState(false);
  const [showUploadLogs, setShowUploadLogs] = useState(false);
  const [uploadLogs, setUploadLogs] = useState<Array<{ key: string; filename: string; uploaded_at: string; size: number }>>([]);
  const [loadingUploadLogs, setLoadingUploadLogs] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [showManualMappingErrors, setShowManualMappingErrors] = useState(false);
  const [showTrialBalanceErrors, setShowTrialBalanceErrors] = useState(false);
  const [glStats, setGlStats] = useState<GLMappingStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  
  // Truncate All state
  const [showTruncateModal, setShowTruncateModal] = useState<boolean>(false);
  const [isTruncating, setIsTruncating] = useState<boolean>(false);
  const [truncateConfirmation, setTruncateConfirmation] = useState<string>("");
  const [truncateError, setTruncateError] = useState<string>("");
  const [truncateSuccess, setTruncateSuccess] = useState<string>("");
  
  // Zoho Sync state
  const [coaSyncFilter, setCoaSyncFilter] = useState<'active' | 'all'>('active');
  const [transactionSyncFilter, setTransactionSyncFilter] = useState<'active' | 'all'>('active');
  const [transactionSyncFromDate, setTransactionSyncFromDate] = useState<string>('');
  const [isSyncingCOA, setIsSyncingCOA] = useState(false);
  const [isSyncingTransactions, setIsSyncingTransactions] = useState(false);
  const [isDebugSyncing, setIsDebugSyncing] = useState(false);
  const [debugSyncResult, setDebugSyncResult] = useState<ZohoSyncTransactionsDebugResponse | null>(null);
  const [showDebugDetails, setShowDebugDetails] = useState(false);
  const [isTruncatingCOA, setIsTruncatingCOA] = useState(false);
  const [isTruncatingTransactions, setIsTruncatingTransactions] = useState(false);
  const [coaSyncResult, setCoaSyncResult] = useState<ZohoSyncAccountsResponse | null>(null);
  const [transactionSyncResult, setTransactionSyncResult] = useState<ZohoSyncTransactionsResponse | null>(null);
  const [truncateResult, setTruncateResult] = useState<{type: 'coa' | 'transactions', success: boolean, message: string} | null>(null);
  const [syncStatus, setSyncStatus] = useState<ZohoSyncStatusResponse['data'] | null>(null);
  const [loadingSyncStatus, setLoadingSyncStatus] = useState(false);
  
  // GL Mapping Sync state
  const [isSyncingGlMappings, setIsSyncingGlMappings] = useState(false);
  const [isTruncatingGlMappings, setIsTruncatingGlMappings] = useState(false);
  const [glMappingsSyncResult, setGlMappingsSyncResult] = useState<ZohoSyncGlMappingsResponse | null>(null);
  const [glMappingsTruncateResult, setGlMappingsTruncateResult] = useState<{success: boolean, message: string} | null>(null);
  const [showClassificationRules, setShowClassificationRules] = useState(false);

  // Databook View (Month Selection) - saved in config_number_format id=2
  const [databookMonthSelection, setDatabookMonthSelection] = useState<DatabookViewScale>("monthwise");
  const [databookViewLoading, setDatabookViewLoading] = useState(true);
  const [isSavingDatabookView, setIsSavingDatabookView] = useState(false);
  const [databookViewSaveResult, setDatabookViewSaveResult] = useState<{ success: boolean; message: string } | null>(null);

  // Number Format (config_number_format id=1)
  const [numberFormatScale, setNumberFormatScale] = useState<NumberFormatScale>("whole");
  const [numberFormatCurrency, setNumberFormatCurrency] = useState<string>("INR");
  const [numberFormatDecimals, setNumberFormatDecimals] = useState<number>(2);
  const [numberFormatSuffix, setNumberFormatSuffix] = useState<string>("");
  const [numberFormatLoading, setNumberFormatLoading] = useState(true);
  const [isSavingNumberFormat, setIsSavingNumberFormat] = useState(false);
  const [numberFormatSaveResult, setNumberFormatSaveResult] = useState<{ success: boolean; message: string } | null>(null);

  // Trial Balance Sync state
  const [trialBalanceSyncFromMonth, setTrialBalanceSyncFromMonth] = useState<string>('');
  const [trialBalanceSyncToMonth, setTrialBalanceSyncToMonth] = useState<string>('');
  const [isSyncingTrialBalance, setIsSyncingTrialBalance] = useState(false);
  const [isTruncatingTrialBalance, setIsTruncatingTrialBalance] = useState(false);
  const [trialBalanceSyncResult, setTrialBalanceSyncResult] = useState<ZohoSyncTrialBalanceResponse | null>(null);
  const [trialBalanceTruncateResult, setTrialBalanceTruncateResult] = useState<{success: boolean, message: string} | null>(null);
  
  // Trial Balance Download state
  const [trialBalanceDownloadFromMonth, setTrialBalanceDownloadFromMonth] = useState<string>('');
  const [trialBalanceDownloadToMonth, setTrialBalanceDownloadToMonth] = useState<string>('');
  const [isDownloadingTrialBalanceMonthWise, setIsDownloadingTrialBalanceMonthWise] = useState(false);

  // Load GL mapping statistics
  const loadGlStats = async () => {
    setLoadingStats(true);
    setStatsError(null);
    try {
      const response = await fetchGLMappingStats();
      if (response.success) {
        setGlStats(response.data);
      }
    } catch (error) {
      setStatsError(error instanceof Error ? error.message : 'Failed to load statistics');
      console.error('Error loading GL mapping stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Load sync status
  const loadSyncStatus = async () => {
    setLoadingSyncStatus(true);
    try {
      const response = await getZohoSyncStatus();
      if (response.success) {
        setSyncStatus(response.data);
      }
    } catch (error) {
      console.error('Error loading sync status:', error);
    } finally {
      setLoadingSyncStatus(false);
    }
  };

  // Load stats on component mount
  useEffect(() => {
    fetchRulesSheetInfo();
    loadGlStats();
    loadSyncStatus();
    (async () => {
      try {
        const scale = await getDatabookViewSetting();
        setDatabookMonthSelection(scale);
      } catch {
        // keep default monthwise
      } finally {
        setDatabookViewLoading(false);
      }
    })();
    (async () => {
      try {
        const setting = await getNumberFormatSetting();
        setNumberFormatScale(setting.scale);
        setNumberFormatCurrency(setting.currency);
        const d = setting.decimals === 1 || setting.decimals === 2 ? setting.decimals : 2;
        setNumberFormatDecimals(d);
        setNumberFormatSuffix(setting.suffix ?? "");
      } catch {
        // keep defaults
      } finally {
        setNumberFormatLoading(false);
      }
    })();
  }, []);

  // Reload stats after successful upload
  useEffect(() => {
    if (uploadResult?.success) {
      loadGlStats();
    }
  }, [uploadResult?.success]);

  // Generate client ID from company name
  const generateClientId = (companyName?: string): string => {
    if (!companyName) return "CLIENT-2024-001";
    const initials = companyName
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
    return `${initials}-2024-001`;
  };

  // Handle raise query button
  const handleRaiseQuery = (): void => {
    navigate("/cfo/dashboard/queries");
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setTrialBalanceResult(null); // Clear previous results
      setShowTrialBalanceErrors(false); // Reset error display state
    }
  };

  // Handle mapping file selection
  const handleMappingFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedMappingFile(file);
      setUploadResult(null); // Clear previous results
      setShowErrors(false); // Reset error display state
    }
  };

  // Handle manual mapping file selection
  const handleManualMappingFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedManualMappingFile(file);
      setManualMappingUploadResult(null); // Clear previous results
      setShowManualMappingErrors(false); // Reset error display state
    }
  };

  // Handle upload button click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle trial balance file upload (validation phase)
  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setTrialBalanceResult(null);
    setShowTrialBalanceErrors(false);

    try {
      const result = await uploadTrialBalance(selectedFile, "validation");
      setTrialBalanceResult(result);
    } catch (error: any) {
      console.error("Upload error:", error);
      setTrialBalanceResult({
        success: false,
        phase: "validation",
        message: error?.message || "Failed to upload file. Please try again.",
        data: {
          total_rows: 0,
          errors: [],
        },
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle trial balance confirmation and upload
  const handleConfirmUpload = async () => {
    if (!selectedFile || !trialBalanceResult) return;

    setIsUploading(true);

    try {
      const result = await uploadTrialBalance(selectedFile, "confirm");
      setTrialBalanceResult(result);
      
      if (result.success) {
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      setTrialBalanceResult({
        success: false,
        phase: "uploaded",
        message: error?.message || "Failed to upload file. Please try again.",
        data: {
          total_rows: 0,
          errors: [],
        },
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle mapping file upload
  const handleUploadMapping = async () => {
    if (!selectedMappingFile) return;

    setIsUploadingMapping(true);
    setUploadResult(null);
    setShowErrors(false);

    try {
      const result = await uploadMapping(selectedMappingFile);
      setUploadResult(result);
      
      // If successful, clear file selection
      if (result.success) {
        setSelectedMappingFile(null);
        if (mappingFileInputRef.current) {
          mappingFileInputRef.current.value = "";
        }
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      
      // Handle unexpected errors
      setUploadResult({
        success: false,
        phase: "validation",
        message: error?.message || "Failed to upload file. Please try again.",
        data: {
          total_rows: 0,
          errors: [],
        },
      });
    } finally {
      setIsUploadingMapping(false);
    }
  };

  // Handle truncate all
  const handleTruncateAll = async () => {
    if (truncateConfirmation !== "TRUNCATE ALL") {
      setTruncateError("Please type 'TRUNCATE ALL' to confirm.");
      return;
    }

    setIsTruncating(true);
    setTruncateError("");
    setTruncateSuccess("");

    try {
      const response: TruncateAllResponse = await truncateAll();

      if (response.success) {
        setTruncateSuccess(
          `Successfully truncated all data. ` +
          `Deleted: ${response.data.gls_deleted} GLs, ` +
          `${response.data.trial_balance_deleted} Trial Balance entries, ` +
          `${response.data.reports_deleted} reports, ` +
          `${response.data.registers_deleted} registers, ` +
          `${response.data.levels_deleted} levels, and ` +
          `${response.data.tags_deleted} tags. ` +
          `Total: ${response.data.total_deleted} records deleted.`
        );
        setShowTruncateModal(false);
        setTruncateConfirmation("");
        // Reload stats
        loadGlStats();
      }
    } catch (err: any) {
      setTruncateError(err.message || "Failed to truncate all data");
    } finally {
      setIsTruncating(false);
    }
  };

  // Handle manual mapping file upload
  const handleUploadManualMapping = async () => {
    if (!selectedManualMappingFile) return;

    setIsUploadingManualMapping(true);
    setManualMappingUploadResult(null);
    setShowManualMappingErrors(false);

    try {
      const result = await uploadManualMapping(selectedManualMappingFile);
      setManualMappingUploadResult(result);
      
      // If successful, clear file selection and reload stats
      if (result.success) {
        setSelectedManualMappingFile(null);
        if (manualMappingFileInputRef.current) {
          manualMappingFileInputRef.current.value = "";
        }
        loadGlStats(); // Reload statistics
      } else {
        // If failed (validation errors or any other failure), clear file selection
        // This allows user to immediately select a corrected file
        setSelectedManualMappingFile(null);
        if (manualMappingFileInputRef.current) {
          manualMappingFileInputRef.current.value = "";
        }
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      
      // Handle unexpected errors - clear file on any error
      setSelectedManualMappingFile(null);
      if (manualMappingFileInputRef.current) {
        manualMappingFileInputRef.current.value = "";
      }
      
      // Handle unexpected errors
      setManualMappingUploadResult({
        success: false,
        phase: "validation",
        message: error?.message || "Failed to upload file. Please try again.",
        data: {
          total_rows: 0,
          errors: [],
        },
      });
    } finally {
      setIsUploadingManualMapping(false);
    }
  };

  // Helper function to pad string with leading zeros
  const padZero = (num: number): string => {
    return num < 10 ? `0${num}` : `${num}`;
  };

  // Helper function to generate timestamp string
  const generateTimestamp = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = padZero(now.getMonth() + 1);
    const day = padZero(now.getDate());
    const hours = padZero(now.getHours());
    const minutes = padZero(now.getMinutes());
    const seconds = padZero(now.getSeconds());
    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
  };

  // Handle template download
  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const companyId = getStoredCompanyId();
      const response = await fetch(
        `${API_BASE_URL}/api/trial-balance/download-template?company_id=${companyId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to download template: ${response.statusText}`
        );
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link with timestamp
      const timestamp = generateTimestamp();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `upload_mapping_template_${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to download template. Please try again."
      );
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle download existing GLs
  const handleDownloadExistingGls = async () => {
    setIsDownloadingExisting(true);
    try {
      const companyId = getStoredCompanyId();
      const response = await fetch(
        `${API_BASE_URL}/api/trial-balance/download-existing-gls?company_id=${companyId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to download existing GLs: ${response.statusText}`
        );
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link with timestamp
      const timestamp = generateTimestamp();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `existing_gls_export_${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to download existing GLs. Please try again."
      );
    } finally {
      setIsDownloadingExisting(false);
    }
  };

  // Handle download manual existing GLs (with ID field)
  const handleDownloadManualExistingGls = async () => {
    setIsDownloadingManualExisting(true);
    try {
      const companyId = getStoredCompanyId();
      const response = await fetch(
        `${API_BASE_URL}/api/trial-balance/download-existing-gls?company_id=${companyId}&include_id=1`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to download existing GLs: ${response.statusText}`
        );
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link with timestamp
      const timestamp = generateTimestamp();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `existing_gls_manual_export_${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to download existing GLs. Please try again."
      );
    } finally {
      setIsDownloadingManualExisting(false);
    }
  };

  // Handle download trial balance template
  const handleDownloadTrialBalanceTemplate = async () => {
    setIsDownloadingTrialBalance(true);
    try {
      const companyId = getStoredCompanyId();
      const response = await fetch(
        `${API_BASE_URL}/api/trial-balance/download-trial-balance-template?company_id=${companyId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to download trial balance template: ${response.statusText}`
        );
      }

      // Get the blob from response
      const blob = await response.blob();

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'trial_balance_template.xlsx';
      if (contentDisposition) {
        // Improved regex to handle both quoted and unquoted filenames
        // Matches: filename="file.xlsx" or filename=file.xlsx or filename*=UTF-8''file.xlsx
        const filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);
        if (filenameMatch && filenameMatch[1]) {
          // Handle RFC 5987 encoded filenames (filename*=UTF-8''...)
          let extractedFilename = filenameMatch[1];
          if (extractedFilename.startsWith("UTF-8''")) {
            extractedFilename = decodeURIComponent(extractedFilename.substring(7));
          }
          filename = extractedFilename.trim();
        }
      }

      // Add timestamp to filename (before extension)
      const timestamp = generateTimestamp();
      // More robust extension extraction - find last dot
      const lastDotIndex = filename.lastIndexOf('.');
      let filenameWithTimestamp: string;
      if (lastDotIndex > 0 && lastDotIndex < filename.length - 1) {
        const baseFilename = filename.substring(0, lastDotIndex);
        const extension = filename.substring(lastDotIndex + 1).trim();
        filenameWithTimestamp = `${baseFilename}_${timestamp}.${extension}`;
      } else {
        // Fallback if no extension found
        filenameWithTimestamp = `${filename}_${timestamp}.xlsx`;
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameWithTimestamp;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to download trial balance template. Please try again."
      );
    } finally {
      setIsDownloadingTrialBalance(false);
    }
  };

  // Handle download trial balance data
  const handleDownloadTrialBalanceData = async () => {
    setIsDownloadingTbData(true);
    try {
      await downloadTrialBalanceData(selectedYear, selectedMonth);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to download trial balance data');
      console.error("Download error:", error);
    } finally {
      setIsDownloadingTbData(false);
    }
  };

  // Fetch rules sheet last-modified info
  const fetchRulesSheetInfo = async () => {
    setRulesSheetInfoLoading(true);
    try {
      const companyId = getStoredCompanyId();
      const response = await fetch(`${API_BASE_URL}/api/trial-balance/rules-sheet-info?company_id=${companyId}`);
      if (response.ok) {
        const data = await response.json();
        setRulesSheetLastModified(data.last_modified ?? null);
      }
    } catch {
      // silently ignore — info is non-critical
    } finally {
      setRulesSheetInfoLoading(false);
    }
  };

  // Handle download rules sheet
  const handleDownloadRulesSheet = async () => {
    setIsDownloadingRules(true);
    try {
      const companyId = getStoredCompanyId();
      const response = await fetch(
        `${API_BASE_URL}/api/trial-balance/download-rules-sheet?company_id=${companyId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to download rules sheet: ${response.statusText}`
        );
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "base_rules_databook.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to download rules sheet. Please try again."
      );
    } finally {
      setIsDownloadingRules(false);
    }
  };

  // Handle file selection for rules sheet
  const handleRulesFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedRulesFile(file);
      setRulesUploadResult(null);
    }
  };

  // Handle upload rules sheet
  const handleUploadRulesSheet = async () => {
    if (!selectedRulesFile) return;

    setIsUploadingRules(true);
    setRulesUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedRulesFile);
      formData.append('company_id', String(getStoredCompanyId()));

      const response = await fetch(
        `${API_BASE_URL}/api/trial-balance/upload-rules-sheet`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to upload rules sheet');
      }

      setRulesUploadResult({
        success: true,
        message: result.message || 'Rules sheet uploaded successfully'
      });

      // Clear file selection on success and refresh the last-modified note
      setSelectedRulesFile(null);
      if (rulesFileInputRef.current) {
        rulesFileInputRef.current.value = "";
      }
      fetchRulesSheetInfo();
      // Refresh upload logs if the panel is open
      if (showUploadLogs) {
        fetchUploadLogs();
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      setRulesUploadResult({
        success: false,
        message: error?.message || "Failed to upload rules sheet. Please try again."
      });
    } finally {
      setIsUploadingRules(false);
    }
  };

  const fetchUploadLogs = async () => {
    setLoadingUploadLogs(true);
    try {
      const companyId = getStoredCompanyId();
      const response = await fetch(`${API_BASE_URL}/api/trial-balance/rules-sheet-upload-logs?company_id=${companyId}`);
      const result = await response.json();
      if (result.success) {
        setUploadLogs(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch upload logs:", error);
    } finally {
      setLoadingUploadLogs(false);
    }
  };

  const handleDownloadBackup = async (key: string) => {
    try {
      const companyId = getStoredCompanyId();
      const response = await fetch(`${API_BASE_URL}/api/trial-balance/download-rules-sheet-backup?key=${encodeURIComponent(key)}&company_id=${companyId}`);
      const result = await response.json();
      if (result.success && result.url) {
        window.open(result.url, '_blank');
      }
    } catch (error) {
      console.error("Failed to download backup:", error);
    }
  };

  const handleToggleUploadLogs = () => {
    const next = !showUploadLogs;
    setShowUploadLogs(next);
    if (next && uploadLogs.length === 0) {
      fetchUploadLogs();
    }
  };

  const handleSaveDatabookView = async () => {
    setIsSavingDatabookView(true);
    setDatabookViewSaveResult(null);
    try {
      await saveDatabookViewSetting(databookMonthSelection);
      setDatabookViewSaveResult({ success: true, message: "Databook view setting saved." });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to save databook view setting.";
      setDatabookViewSaveResult({ success: false, message });
    } finally {
      setIsSavingDatabookView(false);
    }
  };

  const handleSaveNumberFormat = async () => {
    setIsSavingNumberFormat(true);
    setNumberFormatSaveResult(null);
    try {
      await saveNumberFormatSetting({
        scale: numberFormatScale,
        decimals: numberFormatDecimals,
        suffix: numberFormatSuffix,
        currency: numberFormatCurrency,
      });
      setNumberFormatSaveResult({ success: true, message: "Number format setting saved." });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to save number format setting.";
      setNumberFormatSaveResult({ success: false, message });
    } finally {
      setIsSavingNumberFormat(false);
    }
  };

  // Handle COA sync
  const handleSyncCOA = async () => {
    setIsSyncingCOA(true);
    setCoaSyncResult(null);
    
    try {
      const activeOnly = coaSyncFilter === 'active';
      const result = await syncZohoAccounts(activeOnly);
      setCoaSyncResult(result);
      
      // Reload GL stats and sync status after successful sync
      if (result.success) {
        loadGlStats();
        loadSyncStatus();
      }
    } catch (error) {
      setCoaSyncResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to sync COA',
        data: {
          total_fetched: 0,
          inserted: 0,
          updated: 0,
        },
      });
    } finally {
      setIsSyncingCOA(false);
    }
  };

  // Handle Transaction sync
  const handleSyncTransactions = async () => {
    setIsSyncingTransactions(true);
    setTransactionSyncResult(null);

    try {
      const activeOnly = transactionSyncFilter === 'active';
      const fromDate = transactionSyncFromDate || undefined;
      const result = await syncZohoTransactions(activeOnly, fromDate);
      setTransactionSyncResult(result);

      // Reload sync status after successful sync
      if (result.success) {
        loadSyncStatus();
      }
    } catch (error) {
      setTransactionSyncResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to sync transactions',
        data: {
          accounts_synced: 0,
          transactions_synced: 0,
          transactions_inserted: 0,
          transactions_updated: 0,
        },
      });
    } finally {
      setIsSyncingTransactions(false);
    }
  };

  // Dry-run: list accounts that would be synced, WITHOUT hitting Zoho
  const handleDebugSyncTransactions = async () => {
    setIsDebugSyncing(true);
    setDebugSyncResult(null);
    setShowDebugDetails(false);

    try {
      const activeOnly = transactionSyncFilter === 'active';
      const result = await syncZohoTransactionsDebug(activeOnly);
      setDebugSyncResult(result);
    } catch (error) {
      setDebugSyncResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed dry run',
        data: {
          active_only: true,
          from_date_param: null,
          total_accounts: 0,
          breakdown_by_type: {},
          accounts: [],
        },
      });
    } finally {
      setIsDebugSyncing(false);
    }
  };

  // Handle truncate COA
  const handleTruncateCOA = async () => {
    if (!window.confirm('Are you sure you want to delete all Chart of Accounts? This action cannot be undone.')) {
      return;
    }

    setIsTruncatingCOA(true);
    setTruncateResult(null);
    
    try {
      const result = await truncateZohoAccounts();
      setTruncateResult({
        type: 'coa',
        success: result.success,
        message: result.message,
      });
      
      // Reload GL stats and sync status after truncate
      if (result.success) {
        loadGlStats();
        loadSyncStatus();
      }
    } catch (error) {
      setTruncateResult({
        type: 'coa',
        success: false,
        message: error instanceof Error ? error.message : 'Failed to truncate COA',
      });
    } finally {
      setIsTruncatingCOA(false);
    }
  };

  // Handle truncate Transactions
  const handleTruncateTransactions = async () => {
    if (!window.confirm('Are you sure you want to delete all Transactions? This action cannot be undone.')) {
      return;
    }

    setIsTruncatingTransactions(true);
    setTruncateResult(null);
    
    try {
      const result = await truncateZohoTransactions();
      setTruncateResult({
        type: 'transactions',
        success: result.success,
        message: result.message,
      });
      
      // Reload sync status after truncate
      if (result.success) {
        loadSyncStatus();
      }
    } catch (error) {
      setTruncateResult({
        type: 'transactions',
        success: false,
        message: error instanceof Error ? error.message : 'Failed to truncate transactions',
      });
    } finally {
      setIsTruncatingTransactions(false);
    }
  };

  // Handle GL Mappings sync
  const handleSyncGlMappings = async () => {
    setIsSyncingGlMappings(true);
    setGlMappingsSyncResult(null);
    
    try {
      const result = await syncZohoGlMappings();
      setGlMappingsSyncResult(result);
      
      // Reload GL stats after successful sync
      if (result.success) {
        loadGlStats();
      }
    } catch (error) {
      setGlMappingsSyncResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to sync GL mappings',
        data: {
          created: 0,
          updated: 0,
          matched: 0,
          total_processed: 0,
        },
      });
    } finally {
      setIsSyncingGlMappings(false);
    }
  };

  // Handle truncate GL Mappings
  const handleTruncateGlMappings = async () => {
    if (!window.confirm('Are you sure you want to delete ALL GL mappings, levels, and tags? This action cannot be undone.')) {
      return;
    }

    setIsTruncatingGlMappings(true);
    setGlMappingsTruncateResult(null);
    
    try {
      const result = await truncateZohoGlMappings();
      setGlMappingsTruncateResult({
        success: result.success,
        message: result.message,
      });
      
      // Reload GL stats after truncate
      if (result.success) {
        loadGlStats();
      }
    } catch (error) {
      setGlMappingsTruncateResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to truncate GL mappings',
      });
    } finally {
      setIsTruncatingGlMappings(false);
    }
  };

  // Handle Trial Balance sync
  const handleSyncTrialBalance = async () => {
    if (!trialBalanceSyncFromMonth || !trialBalanceSyncToMonth) {
      alert('Please select both From and To months');
      return;
    }

    // Validate month format (YYYY-MM)
    const monthRegex = /^\d{4}-\d{2}$/;
    if (!monthRegex.test(trialBalanceSyncFromMonth) || !monthRegex.test(trialBalanceSyncToMonth)) {
      alert('Invalid month format. Please use YYYY-MM format.');
      return;
    }

    // Convert month selections to date range (first day of from month to last day of to month)
    // From date: first day of the selected month
    const fromDate = `${trialBalanceSyncFromMonth}-01`;
    
    // To date: last day of the selected month
    const [year, month] = trialBalanceSyncToMonth.split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate(); // Get last day of the month
    const toDate = `${trialBalanceSyncToMonth}-${String(lastDay).padStart(2, '0')}`;

    console.log('Syncing trial balance:', { fromDate, toDate, fromMonth: trialBalanceSyncFromMonth, toMonth: trialBalanceSyncToMonth });

    setIsSyncingTrialBalance(true);
    setTrialBalanceSyncResult(null);
    
    try {
      const result = await syncZohoTrialBalance(fromDate, toDate);
      setTrialBalanceSyncResult(result);
    } catch (error) {
      setTrialBalanceSyncResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to sync trial balance',
        data: {
          months_processed: 0,
          accounts_processed: 0,
          records_inserted: 0,
          records_updated: 0,
          records_skipped: 0,
          accounts_without_gl_mapping: 0,
        },
      });
    } finally {
      setIsSyncingTrialBalance(false);
    }
  };

  // Handle truncate Trial Balance
  const handleTruncateTrialBalance = async () => {
    if (!window.confirm('Are you sure you want to delete ALL trial balance data? This action cannot be undone.')) {
      return;
    }

    setIsTruncatingTrialBalance(true);
    setTrialBalanceTruncateResult(null);
    
    try {
      const result = await truncateZohoTrialBalance();
      setTrialBalanceTruncateResult({
        success: result.success,
        message: result.message,
      });
    } catch (error) {
      setTrialBalanceTruncateResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to truncate trial balance',
      });
    } finally {
      setIsTruncatingTrialBalance(false);
    }
  };

  // Handle download Trial Balance Month-wise
  const handleDownloadTrialBalanceMonthWise = async () => {
    if (!trialBalanceDownloadFromMonth || !trialBalanceDownloadToMonth) {
      alert('Please select both From and To months');
      return;
    }

    // Validate month format (YYYY-MM)
    const monthRegex = /^\d{4}-\d{2}$/;
    if (!monthRegex.test(trialBalanceDownloadFromMonth) || !monthRegex.test(trialBalanceDownloadToMonth)) {
      alert('Invalid month format. Please use YYYY-MM format.');
      return;
    }

    // Convert month selections to date range (first day of from month to last day of to month)
    // From date: first day of the selected month
    const fromDate = `${trialBalanceDownloadFromMonth}-01`;
    
    // To date: last day of the selected month
    const [year, month] = trialBalanceDownloadToMonth.split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate(); // Get last day of the month
    const toDate = `${trialBalanceDownloadToMonth}-${String(lastDay).padStart(2, '0')}`;

    setIsDownloadingTrialBalanceMonthWise(true);
    
    try {
      const blob = await downloadZohoTrialBalanceMonthWise(fromDate, toDate);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename from months
      const fromMonthStr = trialBalanceDownloadFromMonth.replace(/-/g, '_');
      const toMonthStr = trialBalanceDownloadToMonth.replace(/-/g, '_');
      link.download = `trial_balance_${fromMonthStr}_to_${toMonthStr}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to download trial balance');
    } finally {
      setIsDownloadingTrialBalanceMonthWise(false);
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Common Header Section */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar
                size="large"
                image="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8Y29tcGFueXxlbnwwfHwwfHx8MA%3D%3D"
              />
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    {company?.companyName || "Acme Corporation"}
                  </span>
                  <Badge variant="warning">High Risk</Badge>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Client ID: {generateClientId(company?.companyName)}
                </span>
              </div>
            </div>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherHelpCircle />}
              onClick={handleRaiseQuery}
            >
              Raise Query
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto p-8">
          <div className="flex w-full max-w-4xl flex-col items-start gap-6">
            {/* Go Back Button */}
            <div className="flex w-full items-start">
              <Button
                variant="neutral-tertiary"
                icon={<FeatherArrowLeft />}
                onClick={() => navigate("/cfo/dashboard")}
              >
                Go Back to Dashboard
              </Button>
            </div>

            {/* Page Title */}
            <div className="flex w-full flex-col items-start gap-2">
              <h1 className="text-heading-1 font-heading-1 text-default-font">
                Trial Balance - For Zoho
              </h1>
              <p className="text-body font-body text-subtext-color">
                Upload your trial balance file to the database. Make sure to use the template format for best results.
              </p>
            </div>

            {/* 1. Sync from Zoho Section */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    1. Sync from Zoho
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      icon={<FeatherRefreshCw className={loadingSyncStatus ? "animate-spin" : ""} />}
                      onClick={loadSyncStatus}
                      disabled={loadingSyncStatus}
                    >
                      {loadingSyncStatus ? "Loading..." : "Refresh"}
                    </Button>
                    <Button
                      variant="neutral-tertiary"
                      icon={<FeatherTrash2 />}
                      onClick={handleTruncateCOA}
                      disabled={isTruncatingCOA || isSyncingCOA}
                    >
                      {isTruncatingCOA ? "Truncating..." : "Truncate COA"}
                    </Button>
                    <Button
                      variant="neutral-tertiary"
                      icon={<FeatherTrash2 />}
                      onClick={handleTruncateTransactions}
                      disabled={isTruncatingTransactions || isSyncingTransactions}
                    >
                      {isTruncatingTransactions ? "Truncating..." : "Truncate Transactions"}
                    </Button>
                  </div>
                </div>
                
                {/* Sync Status Summary */}
                {syncStatus && (
                  <div className="flex w-full flex-col gap-3 pt-2 border-t border-solid border-neutral-border">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Current Status
                    </span>
                    <div className="grid grid-cols-3 gap-3">
                      {/* Total Accounts Card */}
                      <div className="flex flex-col gap-1 rounded-md border border-solid border-neutral-border bg-neutral-50 p-3">
                        <span className="text-caption font-caption text-subtext-color">
                          Total Accounts
                        </span>
                        <span className="text-heading-3 font-heading-3 text-default-font">
                          {syncStatus.accounts_count.toLocaleString()}
                        </span>
                      </div>
                      
                      {/* Active Accounts Card */}
                      <div className="flex flex-col gap-1 rounded-md border border-solid border-green-200 bg-green-50 p-3">
                        <span className="text-caption font-caption text-subtext-color">
                          Active Accounts
                        </span>
                        <span className="text-heading-3 font-heading-3 text-green-700">
                          {syncStatus.active_accounts_count.toLocaleString()}
                        </span>
                      </div>
                      
                      {/* Transactions Card */}
                      <div className="flex flex-col gap-1 rounded-md border border-solid border-blue-200 bg-blue-50 p-3">
                        <span className="text-caption font-caption text-subtext-color">
                          Total Transactions
                        </span>
                        <span className="text-heading-3 font-heading-3 text-blue-700">
                          {syncStatus.transactions_count.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Truncate Result */}
                {truncateResult && (
                  <div className={`flex w-full items-center gap-2 p-3 rounded-md border border-solid ${
                    truncateResult.success 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-red-200 bg-red-50'
                  }`}>
                    {truncateResult.success ? (
                      <FeatherCheckCircle className="text-green-600" />
                    ) : (
                      <FeatherXCircle className="text-red-600" />
                    )}
                    <span className={`text-body font-body ${
                      truncateResult.success ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {truncateResult.message}
                    </span>
                  </div>
                )}
                
                {/* COA Sync */}
                <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
                  <div className="flex w-full items-center justify-between">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Chart of Accounts (COA)
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        Sync Chart of Accounts from Zoho Books
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        value={coaSyncFilter}
                        onChange={(e) => setCoaSyncFilter(e.target.value as 'active' | 'all')}
                        className="px-3 py-2 border border-neutral-border rounded-md text-body font-body bg-default-background"
                        disabled={isSyncingCOA}
                      >
                        <option value="active">Active Only</option>
                        <option value="all">All (Active + Inactive)</option>
                      </select>
                      <Button
                        variant="brand-primary"
                        icon={<FeatherRefreshCw className={isSyncingCOA ? "animate-spin" : ""} />}
                        onClick={handleSyncCOA}
                        disabled={isSyncingCOA}
                      >
                        {isSyncingCOA ? "Syncing..." : "Sync COA"}
                      </Button>
                    </div>
                  </div>
                  
                  {/* COA Sync Summary */}
                  {coaSyncResult && (
                    <div className="flex w-full flex-col items-start gap-2 mt-2 p-3 rounded-md bg-default-background border border-neutral-border">
                      <div className="flex items-center gap-2">
                        {coaSyncResult.success ? (
                          <FeatherCheckCircle className="text-success-600" />
                        ) : (
                          <FeatherXCircle className="text-error-600" />
                        )}
                        <span className="text-body font-body text-default-font">
                          {coaSyncResult.message}
                        </span>
                      </div>
                      {coaSyncResult.success && coaSyncResult.data && (
                        <div className="flex items-center gap-4 text-caption font-caption text-subtext-color ml-6">
                          <span>Total Fetched: {coaSyncResult.data.total_fetched}</span>
                          <span>Inserted: {coaSyncResult.data.inserted}</span>
                          <span>Updated: {coaSyncResult.data.updated}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Transaction Sync */}
                <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
                  <div className="flex w-full items-center justify-between">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Transactions
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        Sync transactions from Zoho Books. Select a date to sync transactions after that date (leave empty to sync from last sync date).
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        value={transactionSyncFilter}
                        onChange={(e) => setTransactionSyncFilter(e.target.value as 'active' | 'all')}
                        className="px-3 py-2 border border-neutral-border rounded-md text-body font-body bg-default-background"
                        disabled={isSyncingTransactions}
                      >
                        <option value="active">Active Only</option>
                        <option value="all">All (Active + Inactive)</option>
                      </select>
                      <div className="flex flex-col gap-1">
                        <input
                          type="date"
                          value={transactionSyncFromDate}
                          onChange={(e) => setTransactionSyncFromDate(e.target.value)}
                          className="px-3 py-2 border border-neutral-border rounded-md text-body font-body bg-default-background"
                          disabled={isSyncingTransactions}
                          placeholder="Sync after date (optional)"
                          title="Select a date to sync transactions after this date"
                        />
                        {transactionSyncFromDate && (
                          <span className="text-caption font-caption text-subtext-color text-xs">
                            Will sync transactions after {transactionSyncFromDate}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="brand-primary"
                        icon={<FeatherRefreshCw className={isSyncingTransactions ? "animate-spin" : ""} />}
                        onClick={handleSyncTransactions}
                        disabled={isSyncingTransactions}
                      >
                        {isSyncingTransactions ? "Syncing..." : "Sync Transactions"}
                      </Button>
                    </div>
                  </div>

                  {/* Dry Run Debug Result */}
                  {debugSyncResult && (
                    <div className="flex w-full flex-col items-start gap-2 mt-2 p-3 rounded-md bg-default-background border border-neutral-border">
                      <div className="flex items-center gap-2">
                        {debugSyncResult.success ? (
                          <FeatherCheckCircle className="text-success-600" />
                        ) : (
                          <FeatherXCircle className="text-error-600" />
                        )}
                        <span className="text-body font-body text-default-font">
                          Dry Run: {debugSyncResult.message}
                        </span>
                      </div>

                      {debugSyncResult.success && debugSyncResult.data && (
                        <>
                          <div className="flex items-center gap-4 text-caption font-caption text-subtext-color ml-6">
                            <span>Total Accounts: <strong>{debugSyncResult.data.total_accounts}</strong></span>
                            <span>Active Only: {debugSyncResult.data.active_only ? "Yes" : "No"}</span>
                            <span>From Date: {debugSyncResult.data.from_date_param || "(none)"}</span>
                          </div>

                          {/* Breakdown by account_type */}
                          <div className="w-full ml-6 mt-1">
                            <div className="text-caption font-caption text-default-font mb-1">Breakdown by account_type:</div>
                            <table className="text-caption font-caption w-auto border border-neutral-border">
                              <thead className="bg-neutral-100">
                                <tr>
                                  <th className="px-2 py-1 text-left border-b border-neutral-border">Account Type</th>
                                  <th className="px-2 py-1 text-right border-b border-neutral-border">Count</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(debugSyncResult.data.breakdown_by_type)
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([type, count]) => (
                                    <tr key={type}>
                                      <td className="px-2 py-1 border-b border-neutral-border">{type}</td>
                                      <td className="px-2 py-1 text-right border-b border-neutral-border">{count}</td>
                                    </tr>
                                  ))}
                                <tr className="bg-neutral-50 font-bold">
                                  <td className="px-2 py-1">TOTAL</td>
                                  <td className="px-2 py-1 text-right">{debugSyncResult.data.total_accounts}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          <div className="ml-6 mt-1">
                            <button
                              type="button"
                              className="text-caption text-brand-600 underline"
                              onClick={() => setShowDebugDetails((v) => !v)}
                            >
                              {showDebugDetails ? "Hide" : "Show"} full account list ({debugSyncResult.data.accounts.length})
                            </button>
                          </div>

                          {showDebugDetails && (
                            <div className="w-full ml-6 mt-1 max-h-96 overflow-auto">
                              <table className="text-caption font-caption w-full border border-neutral-border">
                                <thead className="bg-neutral-100 sticky top-0">
                                  <tr>
                                    <th className="px-2 py-1 text-left border-b border-neutral-border">#</th>
                                    <th className="px-2 py-1 text-left border-b border-neutral-border">Account Name</th>
                                    <th className="px-2 py-1 text-left border-b border-neutral-border">Type</th>
                                    <th className="px-2 py-1 text-left border-b border-neutral-border">Active</th>
                                    <th className="px-2 py-1 text-left border-b border-neutral-border">Last Synced</th>
                                    <th className="px-2 py-1 text-left border-b border-neutral-border">Zoho ID</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {debugSyncResult.data.accounts.map((a, i) => (
                                    <tr key={a.zoho_account_id || i}>
                                      <td className="px-2 py-1 border-b border-neutral-border">{i + 1}</td>
                                      <td className="px-2 py-1 border-b border-neutral-border">{a.account_name}</td>
                                      <td className="px-2 py-1 border-b border-neutral-border">{a.account_type}</td>
                                      <td className="px-2 py-1 border-b border-neutral-border">{String(a.is_active)}</td>
                                      <td className="px-2 py-1 border-b border-neutral-border">{a.last_synced_at || "NULL"}</td>
                                      <td className="px-2 py-1 border-b border-neutral-border font-mono text-xs">{a.zoho_account_id}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Transaction Sync Summary */}
                  {transactionSyncResult && (
                    <div className="flex w-full flex-col items-start gap-2 mt-2 p-3 rounded-md bg-default-background border border-neutral-border">
                      <div className="flex items-center gap-2">
                        {transactionSyncResult.success ? (
                          <FeatherCheckCircle className="text-success-600" />
                        ) : (
                          <FeatherXCircle className="text-error-600" />
                        )}
                        <span className="text-body font-body text-default-font">
                          {transactionSyncResult.message}
                        </span>
                      </div>
                      {transactionSyncResult.success && transactionSyncResult.data && (
                        <div className="flex items-center gap-4 text-caption font-caption text-subtext-color ml-6">
                          <span>Accounts Synced: {transactionSyncResult.data.accounts_synced}</span>
                          <span>Transactions Synced: {transactionSyncResult.data.transactions_synced}</span>
                          <span>Inserted: {transactionSyncResult.data.transactions_inserted}</span>
                          <span>Updated: {transactionSyncResult.data.transactions_updated}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 2. Create GL Classification Section */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    2. Create GL Classification
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      icon={<FeatherRefreshCw />}
                      onClick={loadGlStats}
                      disabled={loadingStats}
                    >
                      {loadingStats ? "Loading..." : "Refresh"}
                    </Button>
                    <Button
                      variant="neutral-tertiary"
                      icon={<FeatherTrash2 />}
                      onClick={handleTruncateGlMappings}
                      disabled={isTruncatingGlMappings || isSyncingGlMappings}
                    >
                      {isTruncatingGlMappings ? "Truncating..." : "Truncate GL Mappings"}
                    </Button>
                  </div>
                </div>
                
                <div className="flex w-full flex-col items-start gap-2">
                  <span className="text-body font-body text-subtext-color">
                    Create GL mappings from synced Zoho Chart of Accounts. After creating GLs, upload Leads (L1, L2) and Tags (T1, T2) using the Upload GL Mapping section below.
                  </span>
                </div>
                
                {/* Classification Rules Info */}
                <div className="flex w-full flex-col gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
                    <button
                    onClick={() => setShowClassificationRules(!showClassificationRules)}
                    className="flex w-full items-center justify-between gap-2 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <FeatherHelpCircle className="text-subtext-color w-4 h-4" />
                      <span className="text-body-bold font-body-bold text-default-font">
                        Classification Rules
                      </span>
                    </div>
                    {showClassificationRules ? <FeatherChevronUp className="w-4 h-4" /> : <FeatherChevronDown className="w-4 h-4" />}
                  </button>
                  
                  {showClassificationRules && (
                    <div className="flex w-full flex-col gap-4 pt-2">
                      <div className="flex w-full flex-col gap-2">
                        <span className="text-body font-body text-subtext-color">
                          Accounts are automatically classified based on their Zoho account type. The system maps account types to:
                        </span>
                        <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-1 ml-2">
                          <li><strong>class_1</strong>: IS (Income Statement) or BS (Balance Sheet)</li>
                          <li><strong>class_2</strong>: Asset, Liability, Equity, Income, or Expense</li>
                        </ul>
                      </div>
                      
                      <div className="flex w-full flex-col gap-3">
                        <div className="flex w-full flex-col gap-2">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Balance Sheet (BS) Accounts
                          </span>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                            <div className="flex flex-col gap-1">
                              <span className="text-body-bold font-body-bold text-green-700">Assets</span>
                              <span className="text-caption font-caption text-subtext-color">cash, bank, accounts_receivable, fixed_asset, intangible_asset, etc.</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-body-bold font-body-bold text-blue-700">Liabilities</span>
                              <span className="text-caption font-caption text-subtext-color">accounts_payable, credit_card, long_term_liability, loans_and_borrowing, etc.</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-body-bold font-body-bold text-purple-700">Equity</span>
                              <span className="text-caption font-caption text-subtext-color">equity accounts</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex w-full flex-col gap-2">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Income Statement (IS) Accounts
                          </span>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            <div className="flex flex-col gap-1">
                              <span className="text-body-bold font-body-bold text-green-700">Income</span>
                              <span className="text-caption font-caption text-subtext-color">income, finance_income, other_income, other_comprehensive_income</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-body-bold font-body-bold text-red-700">Expenses</span>
                              <span className="text-caption font-caption text-subtext-color">expense, cost_of_goods_sold, depreciation_expense, tax_expense, finance_expense, etc.</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex w-full flex-col gap-2 pt-2 border-t border-solid border-neutral-border">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Important Notes
                          </span>
                          <ul className="list-disc list-inside text-caption font-caption text-subtext-color space-y-1 ml-2">
                            <li>Unknown account types will have NULL classifications</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Truncate Result */}
                {glMappingsTruncateResult && (
                  <div className={`flex w-full items-center gap-2 p-3 rounded-md border border-solid ${
                    glMappingsTruncateResult.success 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-red-200 bg-red-50'
                  }`}>
                    {glMappingsTruncateResult.success ? (
                      <FeatherCheckCircle className="text-green-600" />
                    ) : (
                      <FeatherXCircle className="text-red-600" />
                    )}
                    <span className={`text-body font-body ${
                      glMappingsTruncateResult.success ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {glMappingsTruncateResult.message}
                    </span>
                  </div>
                )}
                
                {/* Create GL Mappings Button */}
                <div className="flex w-full items-center justify-end pt-2">
                  <Button
                    variant="brand-primary"
                    icon={<FeatherRefreshCw className={isSyncingGlMappings ? "animate-spin" : ""} />}
                    onClick={handleSyncGlMappings}
                    disabled={isSyncingGlMappings}
                  >
                    {isSyncingGlMappings ? "Creating..." : "Create GL Classification"}
                  </Button>
                </div>
                
                {/* GL Mappings Sync Summary */}
                {glMappingsSyncResult && (
                  <div className="flex w-full flex-col items-start gap-3 mt-2 p-4 rounded-md bg-neutral-50 border border-neutral-border">
                    <div className="flex items-center gap-2">
                      {glMappingsSyncResult.success ? (
                        <FeatherCheckCircle className="text-success-600" />
                      ) : (
                        <FeatherXCircle className="text-error-600" />
                      )}
                      <span className="text-body font-body text-default-font">
                        {glMappingsSyncResult.message}
                      </span>
                    </div>
                    {/* Individual stats (Created, Updated, Matched) are hidden */}
                  </div>
                )}
              </div>
            </div>

            {/* Upload Mapping Section */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    3. Upload GL Mapping
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="destructive-primary"
                      icon={<FeatherAlertTriangle />}
                      onClick={() => setShowTruncateModal(true)}
                    >
                      Truncate All - Delete GLs
                    </Button>
                    <Button
                      variant="neutral-secondary"
                      icon={<FeatherDownload />}
                      onClick={handleDownloadExistingGls}
                      disabled={isDownloadingExisting}
                    >
                      {isDownloadingExisting ? "Downloading..." : "Download Existing GLs"}
                    </Button>
                  </div>
                </div>
                
                <div className="flex w-full flex-col items-start gap-2">
                  <span className="text-body font-body text-subtext-color">
                    This section helps you upload Leads (L1, L2, etc.) and Tags (T1, T2, etc.) mappings for your GL accounts. Upload an Excel file with the mapping data to complete your GL classification setup.
                  </span>
                </div>
                
                {/* File Input */}
                <input
                  ref={mappingFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleMappingFileSelect}
                />

                {/* File Selection Button */}
                <div className="flex w-full items-center gap-4">
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherUpload />}
                    onClick={() => mappingFileInputRef.current?.click()}
                    disabled={isUploadingMapping}
                  >
                    {selectedMappingFile ? "Change File" : "Select Mapping File"}
                  </Button>
                  
                  {selectedMappingFile && (
                    <div className="flex items-center gap-2">
                      <FeatherFileText className="text-body font-body text-subtext-color" />
                      <span className="text-body font-body text-default-font">
                        {selectedMappingFile.name}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        ({(selectedMappingFile.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                {selectedMappingFile && (
                  <div className="flex w-full items-center justify-end pt-2">
                    <Button
                      variant="brand-primary"
                      icon={
                        isUploadingMapping ? (
                          <div className="animate-spin">⏳</div>
                        ) : (
                          <FeatherUpload />
                        )
                      }
                      onClick={handleUploadMapping}
                      disabled={isUploadingMapping || !selectedMappingFile}
                    >
                      {isUploadingMapping ? "Uploading..." : "Upload Mapping"}
                    </Button>
                  </div>
                )}

                {/* Help Text */}
                <div className="flex w-full flex-col items-start gap-2 pt-2">
                  <span className="text-caption font-caption text-subtext-color">
                    Supported formats: XLSX, XLS
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Maximum file size: 10MB
                  </span>
                </div>

                {/* GL Mapping Statistics Summary */}
                <div className="flex w-full flex-col gap-4 pt-4 border-t border-solid border-neutral-border">
                  <div className="flex items-center justify-between">
                    <span className="text-body-bold font-body-bold text-default-font">
                      GL Mapping Summary
                    </span>
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      icon={<FeatherRefreshCw />}
                      onClick={loadGlStats}
                      disabled={loadingStats}
                    >
                      {loadingStats ? "Loading..." : "Refresh"}
                    </Button>
                  </div>
                  
                  {loadingStats ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin">⏳</div>
                      <span className="ml-2 text-caption font-caption text-subtext-color">
                        Loading statistics...
                      </span>
                    </div>
                  ) : statsError ? (
                    <div className="flex items-center gap-2 text-red-600">
                      <FeatherXCircle />
                      <span className="text-caption font-caption">{statsError}</span>
                    </div>
                  ) : glStats ? (
                    <div className="grid grid-cols-4 gap-3">
                      {/* Total GLs Card */}
                      <div className="flex flex-col gap-1 rounded-md border border-solid border-neutral-border bg-default-background p-3">
                        <span className="text-caption font-caption text-subtext-color">
                          Total GLs
                        </span>
                        <span className="text-heading-3 font-heading-3 text-default-font">
                          {glStats.total_gls}
                        </span>
                      </div>
                      
                      {/* Synced from Zoho Card */}
                      <div className="flex flex-col gap-1 rounded-md border border-solid border-green-200 bg-green-50 p-3">
                        <span className="text-caption font-caption text-subtext-color">
                          Synced from Zoho
                        </span>
                        <span className="text-heading-3 font-heading-3 text-green-700">
                          {glStats.synced_from_zoho}
                        </span>
                      </div>
                      
                      {/* Missing Classification Card */}
                      <div className="flex flex-col gap-1 rounded-md border border-solid border-yellow-200 bg-yellow-50 p-3">
                        <span className="text-caption font-caption text-subtext-color">
                          Missing Classification
                        </span>
                        <span className="text-heading-3 font-heading-3 text-yellow-700">
                          {glStats.without_classification}
                        </span>
                      </div>
                      
                      {/* Missing Leads Card */}
                      <div className="flex flex-col gap-1 rounded-md border border-solid border-orange-200 bg-orange-50 p-3">
                        <span className="text-caption font-caption text-subtext-color">
                          Missing Leads
                        </span>
                        <span className="text-heading-3 font-heading-3 text-orange-700">
                          {glStats.without_levels ?? 0}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Upload Results Summary */}
            {uploadResult && (
              <div className={`flex w-full flex-col items-start gap-4 rounded-md border border-solid p-6 ${
                uploadResult.success 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-red-200 bg-red-50'
              }`}>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    {uploadResult.success ? (
                      <FeatherCheckCircle className="text-green-600" />
                    ) : (
                      <FeatherXCircle className="text-red-600" />
                    )}
                    <div className="flex flex-col items-start gap-1">
                      <span className={`text-body-bold font-body-bold ${
                        uploadResult.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {uploadResult.success ? 'Upload Successful' : 'Upload Failed'}
                      </span>
                      <span className={`text-caption font-caption ${
                        uploadResult.success ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {uploadResult.message}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="flex w-full gap-4">
                  <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                    <span className="text-caption font-caption text-subtext-color">Total Rows</span>
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      {uploadResult.data.total_rows}
                    </span>
                  </div>
                  
                  {uploadResult.success && (
                    <>
                      <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                        <span className="text-caption font-caption text-subtext-color">Processed</span>
                        <span className="text-heading-3 font-heading-3 text-green-600">
                          {uploadResult.data.processed || 0}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                        <span className="text-caption font-caption text-subtext-color">New GLs</span>
                        <span className="text-heading-3 font-heading-3 text-green-600">
                          {uploadResult.data.new_gls || 0}
                        </span>
                      </div>
                    </>
                  )}

                  {!uploadResult.success && uploadResult.data.invalid_rows !== undefined && (
                    <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                      <span className="text-caption font-caption text-subtext-color">Invalid Rows</span>
                      <span className="text-heading-3 font-heading-3 text-red-600">
                        {uploadResult.data.invalid_rows}
                      </span>
                    </div>
                  )}
                </div>

                {/* Errors List */}
                {uploadResult.data.errors && uploadResult.data.errors.length > 0 && (
                  <div className="flex w-full flex-col gap-2">
                    <button
                      onClick={() => setShowErrors(!showErrors)}
                      className="flex items-center gap-2 text-body font-body text-default-font hover:text-brand-600"
                    >
                      {showErrors ? <FeatherChevronUp /> : <FeatherChevronDown />}
                      <span>
                        {uploadResult.data.errors.length} Error{uploadResult.data.errors.length !== 1 ? 's' : ''}
                      </span>
                    </button>
                    
                    {showErrors && (
                      <div className="flex w-full flex-col gap-2 max-h-96 overflow-y-auto">
                        {uploadResult.data.errors.map((error: UploadError, index: number) => (
                          <div
                            key={index}
                            className="flex flex-col gap-1 rounded-md bg-white p-3 border border-solid border-red-200"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-body-bold font-body-bold text-red-900">
                                Row {error.row}
                              </span>
                              {error.ledger_name && (
                                <span className="text-body font-body text-subtext-color">
                                  ({error.ledger_name})
                                </span>
                              )}
                              {error.field && (
                                <Badge variant="error">
                                  {error.field}
                                </Badge>
                              )}
                            </div>
                            <span className="text-body font-body text-red-700">
                              {error.error}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 4. Sync Trial Balance Section */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    4. Sync Trial Balance
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="neutral-tertiary"
                      icon={<FeatherTrash2 />}
                      onClick={handleTruncateTrialBalance}
                      disabled={isTruncatingTrialBalance || isSyncingTrialBalance}
                    >
                      {isTruncatingTrialBalance ? "Truncating..." : "Truncate Trial Balance"}
                    </Button>
                  </div>
                </div>
                
                <div className="flex w-full flex-col items-start gap-2">
                  <span className="text-body font-body text-subtext-color">
                    Sync trial balance data from Zoho for a date range. The system will calculate closing balances for each month end date and save them to the trial balance table.
                  </span>
                </div>

                {/* Sync Trial Balance Card */}
                <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
                  <div className="flex w-full items-center justify-between">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Trial Balance
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        Sync trial balance from Zoho for selected date range
                      </span>
                    </div>
                  </div>

                  {/* Month Range Selection */}
                  <div className="flex w-full items-center gap-4">
                    <div className="flex flex-col items-start gap-1">
                      <label className="text-body font-body text-default-font">From Month</label>
                      <input
                        type="month"
                        value={trialBalanceSyncFromMonth}
                        onChange={(e) => setTrialBalanceSyncFromMonth(e.target.value)}
                        className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                        style={{ width: '200px' }}
                        disabled={isSyncingTrialBalance}
                        required
                      />
                      <span className="text-caption font-caption text-subtext-color">
                        Format: YYYY-MM
                      </span>
                    </div>

                    <div className="flex flex-col items-start gap-1">
                      <label className="text-body font-body text-default-font">To Month</label>
                      <input
                        type="month"
                        value={trialBalanceSyncToMonth}
                        onChange={(e) => setTrialBalanceSyncToMonth(e.target.value)}
                        className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                        style={{ width: '200px' }}
                        disabled={isSyncingTrialBalance}
                        required
                      />
                      <span className="text-caption font-caption text-subtext-color">
                        Format: YYYY-MM
                      </span>
                    </div>

                    <div className="flex flex-col items-start gap-1">
                      <label className="text-body font-body text-default-font" style={{ visibility: 'hidden' }}>Sync</label>
                      <Button
                        variant="brand-primary"
                        icon={<FeatherRefreshCw className={isSyncingTrialBalance ? "animate-spin" : ""} />}
                        onClick={handleSyncTrialBalance}
                        disabled={isSyncingTrialBalance || !trialBalanceSyncFromMonth || !trialBalanceSyncToMonth}
                      >
                        {isSyncingTrialBalance ? "Syncing..." : "Sync Trial Balance"}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Sync Result */}
                {trialBalanceSyncResult && (
                  <div className={`flex w-full flex-col items-start gap-3 rounded-md p-4 ${
                    trialBalanceSyncResult.success 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      {trialBalanceSyncResult.success ? (
                        <FeatherCheckCircle className="text-green-600" />
                      ) : (
                        <FeatherXCircle className="text-red-600" />
                      )}
                      <span className={`text-body font-body ${
                        trialBalanceSyncResult.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {trialBalanceSyncResult.message}
                      </span>
                    </div>
                    
                    {trialBalanceSyncResult.success && trialBalanceSyncResult.data && (
                      <div className="flex w-full gap-4">
                        <div className="flex flex-col gap-1 rounded-md bg-white p-3 border border-solid border-neutral-border">
                          <span className="text-caption font-caption text-subtext-color">Months Processed</span>
                          <span className="text-heading-3 font-heading-3 text-default-font">
                            {trialBalanceSyncResult.data.months_processed}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1 rounded-md bg-white p-3 border border-solid border-neutral-border">
                          <span className="text-caption font-caption text-subtext-color">Accounts Processed</span>
                          <span className="text-heading-3 font-heading-3 text-default-font">
                            {trialBalanceSyncResult.data.accounts_processed}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1 rounded-md bg-white p-3 border border-solid border-neutral-border">
                          <span className="text-caption font-caption text-subtext-color">Records Inserted</span>
                          <span className="text-heading-3 font-heading-3 text-green-600">
                            {trialBalanceSyncResult.data.records_inserted}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1 rounded-md bg-white p-3 border border-solid border-neutral-border">
                          <span className="text-caption font-caption text-subtext-color">Records Updated</span>
                          <span className="text-heading-3 font-heading-3 text-green-600">
                            {trialBalanceSyncResult.data.records_updated}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Truncate Result */}
                {trialBalanceTruncateResult && (
                  <div className={`flex w-full items-center gap-3 rounded-md p-4 ${
                    trialBalanceTruncateResult.success 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    {trialBalanceTruncateResult.success ? (
                      <FeatherCheckCircle className="text-green-600" />
                    ) : (
                      <FeatherXCircle className="text-red-600" />
                    )}
                    <span className={`text-body font-body ${
                      trialBalanceTruncateResult.success ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {trialBalanceTruncateResult.message}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* 5. Download Trial Balance Section */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    5. Download Trial Balance
                  </span>
                </div>
                
                <div className="flex w-full flex-col items-start gap-2">
                  <span className="text-body font-body text-subtext-color">
                    Download trial balance data as Excel file with GL mapping information (Ledger Name, Class 1, Class 2, Zoho Account ID, Levels, Tags) and monthly amounts.
                  </span>
                </div>

                {/* Month Range Selection */}
                <div className="flex w-full items-center gap-4">
                  <div className="flex flex-col items-start gap-1">
                    <label className="text-body font-body text-default-font">From Month</label>
                    <input
                      type="month"
                      value={trialBalanceDownloadFromMonth}
                      onChange={(e) => setTrialBalanceDownloadFromMonth(e.target.value)}
                      className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                      style={{ width: '200px' }}
                      required
                    />
                    <span className="text-caption font-caption text-subtext-color">
                      Format: YYYY-MM
                    </span>
                  </div>

                  <div className="flex flex-col items-start gap-1">
                    <label className="text-body font-body text-default-font">To Month</label>
                    <input
                      type="month"
                      value={trialBalanceDownloadToMonth}
                      onChange={(e) => setTrialBalanceDownloadToMonth(e.target.value)}
                      className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                      style={{ width: '200px' }}
                      required
                    />
                    <span className="text-caption font-caption text-subtext-color">
                      Format: YYYY-MM
                    </span>
                  </div>
                </div>

                {/* Download Button */}
                <div className="flex w-full items-center justify-end">
                  <Button
                    variant="brand-primary"
                    icon={
                      isDownloadingTrialBalanceMonthWise ? (
                        <div className="animate-spin">⏳</div>
                      ) : (
                        <FeatherDownload />
                      )
                    }
                    onClick={handleDownloadTrialBalanceMonthWise}
                    disabled={isDownloadingTrialBalanceMonthWise || !trialBalanceDownloadFromMonth || !trialBalanceDownloadToMonth}
                  >
                    {isDownloadingTrialBalanceMonthWise ? "Downloading..." : "Download Trial Balance"}
                  </Button>
                </div>
              </div>
            </div>

            {/* TB from Manual upload Section */}
            <div className="flex w-full flex-col items-start gap-4 mt-8">
              <h1 className="text-heading-1 font-heading-1 text-default-font">
                TB from Manual upload
              </h1>
            </div>

            {/* 1. Upload GL Mappings Section (Manual - without Zoho Account ID) */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    1. Upload GL Mappings
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="neutral-secondary"
                      icon={<FeatherDownload />}
                      onClick={handleDownloadManualExistingGls}
                      disabled={isDownloadingManualExisting}
                    >
                      {isDownloadingManualExisting ? "Downloading..." : "Download Existing GLs"}
                    </Button>
                    <Button
                      variant="destructive-secondary"
                      icon={<FeatherTrash2 />}
                      onClick={() => navigate("/cfo/tb/delete-gls")}
                    >
                      Delete GLs
                    </Button>
                    <Button
                      variant="destructive-primary"
                      icon={<FeatherAlertTriangle />}
                      onClick={() => setShowTruncateModal(true)}
                    >
                      Truncate All
                    </Button>
                  </div>
                </div>
                
                <div className="flex w-full flex-col items-start gap-2">
                  <span className="text-body font-body text-subtext-color">
                    This section helps you upload Leads (L1, L2, etc.) and Tags (T1, T2, etc.) mappings for your GL accounts. Upload an Excel file with the mapping data. The file should include an ID column (from downloaded existing GLs) to update existing records. Zoho Account ID column will be ignored if present.
                  </span>
                </div>
                
                {/* File Input */}
                <input
                  ref={manualMappingFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleManualMappingFileSelect}
                />

                {/* File Selection Button */}
                <div className="flex w-full items-center gap-4">
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherUpload />}
                    onClick={() => manualMappingFileInputRef.current?.click()}
                    disabled={isUploadingManualMapping}
                  >
                    {selectedManualMappingFile ? "Change File" : "Select Mapping File"}
                  </Button>
                  
                  {selectedManualMappingFile && (
                    <div className="flex items-center gap-2">
                      <FeatherFileText className="text-body font-body text-subtext-color" />
                      <span className="text-body font-body text-default-font">
                        {selectedManualMappingFile.name}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        ({(selectedManualMappingFile.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                {selectedManualMappingFile && (
                  <div className="flex w-full items-center justify-end pt-2">
                    <Button
                      variant="brand-primary"
                      icon={
                        isUploadingManualMapping ? (
                          <div className="animate-spin">⏳</div>
                        ) : (
                          <FeatherUpload />
                        )
                      }
                      onClick={handleUploadManualMapping}
                      disabled={isUploadingManualMapping || !selectedManualMappingFile}
                    >
                      {isUploadingManualMapping ? "Uploading..." : "Upload Mapping"}
                    </Button>
                  </div>
                )}

                {/* Help Text */}
                <div className="flex w-full flex-col items-start gap-2 pt-2">
                  <span className="text-caption font-caption text-subtext-color">
                    Supported formats: XLSX, XLS
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Maximum file size: 10MB
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Include ID column to update existing GLs. Zoho Account ID column will be ignored.
                  </span>
                </div>
              </div>
            </div>

            {/* Manual Upload Results Summary */}
            {manualMappingUploadResult && (
              <div className={`flex w-full flex-col items-start gap-4 rounded-md border border-solid p-6 ${
                manualMappingUploadResult.success 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-red-200 bg-red-50'
              }`}>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    {manualMappingUploadResult.success ? (
                      <FeatherCheckCircle className="text-green-600" />
                    ) : (
                      <FeatherXCircle className="text-red-600" />
                    )}
                    <div className="flex flex-col items-start gap-1">
                      <span className={`text-body-bold font-body-bold ${
                        manualMappingUploadResult.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {manualMappingUploadResult.success ? 'Upload Successful' : 'Upload Failed'}
                      </span>
                      <span className={`text-caption font-caption ${
                        manualMappingUploadResult.success ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {manualMappingUploadResult.message}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="flex w-full gap-4">
                  <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                    <span className="text-caption font-caption text-subtext-color">Total Rows</span>
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      {manualMappingUploadResult.data.total_rows}
                    </span>
                  </div>
                  
                  {manualMappingUploadResult.success && (
                    <>
                      <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                        <span className="text-caption font-caption text-subtext-color">Processed</span>
                        <span className="text-heading-3 font-heading-3 text-green-600">
                          {manualMappingUploadResult.data.processed || 0}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                        <span className="text-caption font-caption text-subtext-color">New GLs</span>
                        <span className="text-heading-3 font-heading-3 text-green-600">
                          {manualMappingUploadResult.data.new_gls || 0}
                        </span>
                      </div>
                      {manualMappingUploadResult.data.updated_gls !== undefined && (
                        <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                          <span className="text-caption font-caption text-subtext-color">Updated GLs</span>
                          <span className="text-heading-3 font-heading-3 text-blue-600">
                            {manualMappingUploadResult.data.updated_gls || 0}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {!manualMappingUploadResult.success && manualMappingUploadResult.data.invalid_rows !== undefined && (
                    <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                      <span className="text-caption font-caption text-subtext-color">Invalid Rows</span>
                      <span className="text-heading-3 font-heading-3 text-red-600">
                        {manualMappingUploadResult.data.invalid_rows}
                      </span>
                    </div>
                  )}
                </div>

                {/* Errors List */}
                {manualMappingUploadResult.data.errors && manualMappingUploadResult.data.errors.length > 0 && (
                  <div className="flex w-full flex-col gap-2">
                    <button
                      onClick={() => setShowManualMappingErrors(!showManualMappingErrors)}
                      className="flex items-center gap-2 text-body font-body text-default-font hover:text-brand-600"
                    >
                      {showManualMappingErrors ? <FeatherChevronUp /> : <FeatherChevronDown />}
                      <span>
                        {manualMappingUploadResult.data.errors.length} Error{manualMappingUploadResult.data.errors.length !== 1 ? 's' : ''}
                      </span>
                    </button>
                    
                    {showManualMappingErrors && (
                      <div className="flex w-full flex-col gap-2 max-h-96 overflow-y-auto">
                        {manualMappingUploadResult.data.errors.map((error: UploadError, index: number) => (
                          <div
                            key={index}
                            className="flex flex-col gap-1 p-3 rounded-md bg-white border border-solid border-red-200"
                          >
                            <div className="flex items-center gap-2">
                              <FeatherXCircle className="text-red-600" />
                              <span className="text-body font-body text-red-900">
                                Row {error.row}: {error.ledger_name || 'Unknown'}
                              </span>
                            </div>
                            <span className="text-caption font-caption text-red-700 ml-6">
                              {error.error}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Upload Section (Trial Balance - Keep existing) */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    2. Upload Trial Balance ( Monthly )
                  </span>
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherDownload />}
                    onClick={handleDownloadTrialBalanceTemplate}
                    disabled={isDownloadingTrialBalance}
                  >
                    {isDownloadingTrialBalance ? "Downloading..." : "Download Template"}
                  </Button>
                </div>
                
                {/* File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />

                {/* File Selection Button */}
                <div className="flex w-full items-center gap-4">
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherUpload />}
                    onClick={handleUploadClick}
                    disabled={isUploading}
                  >
                    {selectedFile ? "Change File" : "Select File"}
                  </Button>
                  
                  {selectedFile && (
                    <div className="flex items-center gap-2">
                      <FeatherFileText className="text-body font-body text-subtext-color" />
                      <span className="text-body font-body text-default-font">
                        {selectedFile.name}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        ({(selectedFile.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                {selectedFile && (
                  <div className="flex w-full items-center justify-end pt-2">
                    <Button
                      variant="brand-primary"
                      icon={
                        isUploading ? (
                          <div className="animate-spin">⏳</div>
                        ) : (
                          <FeatherUpload />
                        )
                      }
                      onClick={handleUpload}
                      disabled={isUploading || !selectedFile}
                    >
                      {isUploading ? "Validating..." : "Validate & Upload"}
                    </Button>
                  </div>
                )}

                {/* Help Text */}
                <div className="flex w-full flex-col items-start gap-2 pt-2">
                  <span className="text-caption font-caption text-subtext-color">
                    Supported formats: XLSX, XLS
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Maximum file size: 10MB
                  </span>
                </div>
              </div>
            </div>

            {/* Trial Balance Upload Results Summary */}
            {trialBalanceResult && (
              <div className={`flex w-full flex-col items-start gap-4 rounded-md border border-solid p-6 ${
                trialBalanceResult.success && trialBalanceResult.phase === "uploaded"
                  ? 'border-green-200 bg-green-50' 
                  : trialBalanceResult.success && trialBalanceResult.phase === "validation"
                  ? 'border-blue-200 bg-blue-50'
                  : 'border-red-200 bg-red-50'
              }`}>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-3">
                    {trialBalanceResult.success && trialBalanceResult.phase === "uploaded" ? (
                      <FeatherCheckCircle className="text-green-600" />
                    ) : trialBalanceResult.success && trialBalanceResult.phase === "validation" ? (
                      <FeatherCheckCircle className="text-blue-600" />
                    ) : (
                      <FeatherXCircle className="text-red-600" />
                    )}
                    <div className="flex flex-col items-start gap-1">
                      <span className={`text-body-bold font-body-bold ${
                        trialBalanceResult.success && trialBalanceResult.phase === "uploaded"
                          ? 'text-green-900' 
                          : trialBalanceResult.success && trialBalanceResult.phase === "validation"
                          ? 'text-blue-900'
                          : 'text-red-900'
                      }`}>
                        {trialBalanceResult.success && trialBalanceResult.phase === "uploaded"
                          ? 'Upload Successful' 
                          : trialBalanceResult.success && trialBalanceResult.phase === "validation"
                          ? 'Validation Successful'
                          : 'Validation Failed'}
                      </span>
                      <span className={`text-caption font-caption ${
                        trialBalanceResult.success && trialBalanceResult.phase === "uploaded"
                          ? 'text-green-700' 
                          : trialBalanceResult.success && trialBalanceResult.phase === "validation"
                          ? 'text-blue-700'
                          : 'text-red-700'
                      }`}>
                        {trialBalanceResult.message}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Warning for Existing Data */}
                {trialBalanceResult.data.has_existing_data && trialBalanceResult.data.warning_message && (
                  <div className="flex w-full items-start gap-3 rounded-md border border-solid border-yellow-300 bg-yellow-50 p-4">
                    <FeatherAlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="flex flex-col gap-1">
                      <span className="text-body-bold font-body-bold text-yellow-900">
                        Data Overwrite Warning
                      </span>
                      <span className="text-body font-body text-yellow-800">
                        {trialBalanceResult.data.warning_message}
                      </span>
                      {trialBalanceResult.data.existing_months && trialBalanceResult.data.existing_months.length > 0 && (
                        <div className="flex flex-col gap-1 mt-2">
                          <span className="text-caption font-caption text-yellow-700 font-semibold">
                            Affected Months:
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {trialBalanceResult.data.existing_months.map((month: any, index: number) => (
                              <Badge key={index} variant="warning">
                                {month.header} ({month.record_count} record{month.record_count !== 1 ? 's' : ''})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Summary Stats */}
                <div className="flex w-full gap-4">
                  <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                    <span className="text-caption font-caption text-subtext-color">Total Rows</span>
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      {trialBalanceResult.data.total_rows}
                    </span>
                  </div>
                  
                  {trialBalanceResult.phase === "validation" && (
                    <>
                      <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                        <span className="text-caption font-caption text-subtext-color">Valid Rows</span>
                        <span className="text-heading-3 font-heading-3 text-blue-600">
                          {trialBalanceResult.data.valid_rows || 0}
                        </span>
                      </div>
                      {trialBalanceResult.data.duplicate_in_file !== undefined && trialBalanceResult.data.duplicate_in_file > 0 && (
                        <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-red-200">
                          <span className="text-caption font-caption text-subtext-color">Duplicates in File</span>
                          <span className="text-heading-3 font-heading-3 text-red-600">
                            {trialBalanceResult.data.duplicate_in_file}
                          </span>
                        </div>
                      )}
                      {trialBalanceResult.data.duplicate_in_db !== undefined && trialBalanceResult.data.duplicate_in_db > 0 && (
                        <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-red-200">
                          <span className="text-caption font-caption text-subtext-color">Duplicates in DB</span>
                          <span className="text-heading-3 font-heading-3 text-red-600">
                            {trialBalanceResult.data.duplicate_in_db}
                          </span>
                        </div>
                      )}
                      {trialBalanceResult.data.invalid_rows !== undefined && trialBalanceResult.data.invalid_rows > 0 && (
                        <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-red-200">
                          <span className="text-caption font-caption text-subtext-color">Invalid Rows</span>
                          <span className="text-heading-3 font-heading-3 text-red-600">
                            {trialBalanceResult.data.invalid_rows}
                          </span>
                        </div>
                      )}
                      {trialBalanceResult.data.month && (
                        <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                          <span className="text-caption font-caption text-subtext-color">Month</span>
                          <span className="text-heading-3 font-heading-3 text-default-font">
                            {trialBalanceResult.data.month}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {trialBalanceResult.phase === "uploaded" && (
                    <>
                      <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                        <span className="text-caption font-caption text-subtext-color">Uploaded</span>
                        <span className="text-heading-3 font-heading-3 text-green-600">
                          {trialBalanceResult.data.uploaded || 0}
                        </span>
                      </div>
                      {trialBalanceResult.data.month && (
                        <div className="flex flex-col gap-1 rounded-md bg-white p-4 border border-solid border-neutral-border">
                          <span className="text-caption font-caption text-subtext-color">Month</span>
                          <span className="text-heading-3 font-heading-3 text-default-font">
                            {trialBalanceResult.data.month}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Confirmation Button (only show if validation passed) */}
                {trialBalanceResult.success && trialBalanceResult.phase === "validation" && (
                  <div className="flex w-full items-center justify-end pt-2">
                    <Button
                      variant="brand-primary"
                      icon={<FeatherCheckCircle />}
                      onClick={handleConfirmUpload}
                      disabled={isUploading}
                    >
                      {isUploading ? "Uploading..." : "Confirm & Upload to Database"}
                    </Button>
                  </div>
                )}

                {/* Errors List */}
                {trialBalanceResult.data.errors && trialBalanceResult.data.errors.length > 0 && (
                  <div className="flex w-full flex-col gap-2">
                    <button
                      onClick={() => setShowTrialBalanceErrors(!showTrialBalanceErrors)}
                      className="flex items-center gap-2 text-body font-body text-default-font hover:text-brand-600"
                    >
                      {showTrialBalanceErrors ? <FeatherChevronUp /> : <FeatherChevronDown />}
                      <span>
                        {trialBalanceResult.data.errors.length} Error{trialBalanceResult.data.errors.length !== 1 ? 's' : ''}
                      </span>
                    </button>
                    
                    {showTrialBalanceErrors && (
                      <div className="flex w-full flex-col gap-2 max-h-96 overflow-y-auto">
                        {trialBalanceResult.data.errors.map((error: UploadError, index: number) => (
                          <div
                            key={index}
                            className="flex flex-col gap-1 rounded-md bg-white p-3 border border-solid border-red-200"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-body-bold font-body-bold text-red-900">
                                Row {error.row}
                              </span>
                              {error.ledger_name && (
                                <span className="text-body font-body text-subtext-color">
                                  ({error.ledger_name})
                                </span>
                              )}
                              {error.field && (
                                <Badge variant="error">
                                  {error.field}
                                </Badge>
                              )}
                            </div>
                            <span className="text-body font-body text-red-700">
                              {error.error}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Configuration */}
            <div className="flex w-full flex-col items-start gap-4 mt-8">
              <h1 className="text-heading-1 font-heading-1 text-default-font">
                Configuration
              </h1>
            </div>

            {/* Upload Rules Sheet Section */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-center justify-between">
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Upload Rules Sheet
                    </span>
                    {rulesSheetInfoLoading ? (
                      <span className="text-caption font-caption text-subtext-color">
                        Checking last upload…
                      </span>
                    ) : rulesSheetLastModified ? (
                      <span className="text-caption font-caption text-subtext-color">
                        Last updated:{" "}
                        {new Date(rulesSheetLastModified).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </span>
                    ) : (
                      <span className="text-caption font-caption text-subtext-color">
                        No rules sheet uploaded yet
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="neutral-secondary"
                      icon={<FeatherFileText />}
                      onClick={handleToggleUploadLogs}
                    >
                      Upload Logs
                    </Button>
                    <Button
                      variant="neutral-secondary"
                      icon={<FeatherDownload />}
                      onClick={handleDownloadRulesSheet}
                      disabled={isDownloadingRules}
                    >
                      {isDownloadingRules ? "Downloading..." : "Download"}
                    </Button>
                  </div>
                </div>

                {/* Upload Logs Section */}
                {showUploadLogs && (
                  <div className="flex w-full flex-col gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-body font-body font-semibold text-default-font">
                        Recent Uploads
                      </span>
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        icon={<FeatherRefreshCw className={loadingUploadLogs ? "animate-spin" : ""} />}
                        onClick={fetchUploadLogs}
                        disabled={loadingUploadLogs}
                      >
                        Refresh
                      </Button>
                    </div>
                    {loadingUploadLogs ? (
                      <div className="flex items-center justify-center py-4">
                        <span className="text-caption font-caption text-subtext-color">Loading upload logs...</span>
                      </div>
                    ) : uploadLogs.length === 0 ? (
                      <div className="flex items-center justify-center py-4">
                        <span className="text-caption font-caption text-subtext-color">No upload history found</span>
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-neutral-border">
                            <th className="py-2 text-left text-caption font-semibold text-subtext-color">File Name</th>
                            <th className="py-2 text-left text-caption font-semibold text-subtext-color">Uploaded At</th>
                            <th className="py-2 text-right text-caption font-semibold text-subtext-color">Size</th>
                            <th className="py-2 text-right text-caption font-semibold text-subtext-color"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadLogs.map((log, idx) => (
                            <tr key={idx} className="border-b border-neutral-border last:border-0">
                              <td className="py-2 text-body text-default-font">{log.filename}</td>
                              <td className="py-2 text-body text-subtext-color">
                                {new Date(log.uploaded_at + " UTC").toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })}
                              </td>
                              <td className="py-2 text-right text-body text-subtext-color">
                                {(log.size / 1024).toFixed(1)} KB
                              </td>
                              <td className="py-2 text-right">
                                <Button
                                  variant="neutral-tertiary"
                                  size="small"
                                  icon={<FeatherDownload />}
                                  onClick={() => handleDownloadBackup(log.key)}
                                >
                                  Download
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* File Input */}
                <input
                  ref={rulesFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleRulesFileSelect}
                />

                {/* File Selection Button */}
                <div className="flex w-full items-center gap-4">
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherUpload />}
                    onClick={() => rulesFileInputRef.current?.click()}
                    disabled={isUploadingRules}
                  >
                    {selectedRulesFile ? "Change File" : "Select File"}
                  </Button>
                  
                  {selectedRulesFile && (
                    <div className="flex items-center gap-2">
                      <FeatherFileText className="text-body font-body text-subtext-color" />
                      <span className="text-body font-body text-default-font">
                        {selectedRulesFile.name}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        ({(selectedRulesFile.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                {selectedRulesFile && (
                  <div className="flex w-full items-center justify-end pt-2">
                    <Button
                      variant="brand-primary"
                      icon={
                        isUploadingRules ? (
                          <div className="animate-spin">⏳</div>
                        ) : (
                          <FeatherUpload />
                        )
                      }
                      onClick={handleUploadRulesSheet}
                      disabled={isUploadingRules || !selectedRulesFile}
                    >
                      {isUploadingRules ? "Uploading..." : "Upload Rules Sheet"}
                    </Button>
                  </div>
                )}

                {/* Upload Result */}
                {rulesUploadResult && (
                  <div className={`flex w-full items-center gap-3 rounded-md p-4 ${
                    rulesUploadResult.success 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    {rulesUploadResult.success ? (
                      <FeatherCheckCircle className="text-green-600" />
                    ) : (
                      <FeatherXCircle className="text-red-600" />
                    )}
                    <span className={`text-body font-body ${
                      rulesUploadResult.success ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {rulesUploadResult.message}
                    </span>
                  </div>
                )}

                {/* Help Text */}
                <div className="flex w-full flex-col items-start gap-2 pt-2">
                  <span className="text-caption font-caption text-subtext-color">
                    Supported formats: XLSX, XLS
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Maximum file size: 10MB
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Note: The existing file will be automatically backed up before replacement.
                  </span>
                </div>
              </div>
            </div>

            {/* Databook View - Month Selection for Databook */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Databook View
              </span>
              <div className="flex w-full flex-col gap-3">
                <span className="text-body font-body text-default-font">
                  Month Selection for Databook
                </span>
                <RadioGroup
                  value={databookMonthSelection}
                  onValueChange={(value) => {
                    setDatabookMonthSelection(value as DatabookViewScale);
                    setDatabookViewSaveResult(null);
                  }}
                  horizontal
                >
                  <RadioGroup.Option value="6months" label="6 Months" />
                  <RadioGroup.Option value="yearly" label="Yearly" />
                  <RadioGroup.Option value="monthwise" label="Monthwise" />
                  <RadioGroup.Option value="quarterly" label="Quarterly" />
                </RadioGroup>
              </div>
              {databookViewSaveResult && (
                <div
                  className={`flex w-full items-center gap-3 rounded-md p-4 ${
                    databookViewSaveResult.success
                      ? "bg-green-50 border border-green-200"
                      : "bg-red-50 border border-red-200"
                  }`}
                >
                  {databookViewSaveResult.success ? (
                    <FeatherCheckCircle className="text-green-600" />
                  ) : (
                    <FeatherXCircle className="text-red-600" />
                  )}
                  <span
                    className={`text-body font-body ${
                      databookViewSaveResult.success ? "text-green-900" : "text-red-900"
                    }`}
                  >
                    {databookViewSaveResult.message}
                  </span>
                </div>
              )}
              <div className="flex w-full justify-end pt-2">
                <Button
                  variant="brand-primary"
                  onClick={handleSaveDatabookView}
                  disabled={databookViewLoading || isSavingDatabookView}
                >
                  {isSavingDatabookView ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>

            {/* Number Format - config_number_format id=1 */}
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Number Format
              </span>
              <div className="flex w-full flex-col gap-4">
                <div className="flex w-full flex-col gap-2">
                  <span className="text-body font-body text-default-font">
                    Scale
                  </span>
                  <RadioGroup
                    value={numberFormatScale}
                    onValueChange={(value) => {
                      setNumberFormatScale(value as NumberFormatScale);
                      setNumberFormatSaveResult(null);
                    }}
                    horizontal
                  >
                    <RadioGroup.Option value="whole" label="Whole" />
                    <RadioGroup.Option value="thousand" label="Thousand" />
                    <RadioGroup.Option value="lakh" label="Lakh" />
                    <RadioGroup.Option value="million" label="Million" />
                    <RadioGroup.Option value="crore" label="Crore" />
                    <RadioGroup.Option value="billion" label="Billion" />
                  </RadioGroup>
                </div>
                <div className="flex w-full flex-col gap-2">
                  <span className="text-body font-body text-default-font">
                    Currency
                  </span>
                  <input
                    type="text"
                    value={numberFormatCurrency}
                    onChange={(e) => {
                      setNumberFormatCurrency(e.target.value);
                      setNumberFormatSaveResult(null);
                    }}
                    className="w-full max-w-xs rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                    placeholder="INR"
                  />
                </div>
                <div className="flex w-full flex-col gap-2">
                  <span className="text-body font-body text-default-font">
                    Decimals
                  </span>
                  <RadioGroup
                    value={String(numberFormatDecimals)}
                    onValueChange={(value) => {
                      setNumberFormatDecimals(parseInt(value, 10));
                      setNumberFormatSaveResult(null);
                    }}
                    horizontal
                  >
                    <RadioGroup.Option value="1" label="1" />
                    <RadioGroup.Option value="2" label="2" />
                  </RadioGroup>
                </div>
                <div className="flex w-full flex-col gap-2">
                  <span className="text-body font-body text-default-font">
                    Suffix (e.g. M, L, Cr)
                  </span>
                  <input
                    type="text"
                    value={numberFormatSuffix}
                    onChange={(e) => {
                      setNumberFormatSuffix(e.target.value);
                      setNumberFormatSaveResult(null);
                    }}
                    className="w-full max-w-xs rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                    placeholder="M"
                  />
                </div>
              </div>
              {numberFormatSaveResult && (
                <div
                  className={`flex w-full items-center gap-3 rounded-md p-4 ${
                    numberFormatSaveResult.success
                      ? "bg-green-50 border border-green-200"
                      : "bg-red-50 border border-red-200"
                  }`}
                >
                  {numberFormatSaveResult.success ? (
                    <FeatherCheckCircle className="text-green-600" />
                  ) : (
                    <FeatherXCircle className="text-red-600" />
                  )}
                  <span
                    className={`text-body font-body ${
                      numberFormatSaveResult.success ? "text-green-900" : "text-red-900"
                    }`}
                  >
                    {numberFormatSaveResult.message}
                  </span>
                </div>
              )}
              <div className="flex w-full justify-end pt-2">
                <Button
                  variant="brand-primary"
                  onClick={handleSaveNumberFormat}
                  disabled={numberFormatLoading || isSavingNumberFormat}
                >
                  {isSavingNumberFormat ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Truncate All Confirmation Modal */}
      {showTruncateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-default-background rounded-lg border border-solid border-neutral-border p-6 max-w-lg w-full mx-4">
            <div className="flex flex-col items-start gap-4">
              <div className="flex items-start gap-3 w-full">
                <FeatherAlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col items-start gap-2 flex-1">
                  <h2 className="text-heading-3 font-heading-3 text-default-font">
                    Truncate All GL Data
                  </h2>
                  <div className="flex flex-col items-start gap-3 w-full">
                    <div className="flex w-full items-start gap-3 rounded-md border border-solid border-red-200 bg-red-50 p-3">
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-body font-body font-semibold text-red-900">
                          ⚠️ WARNING: This action cannot be undone!
                        </span>
                        <span className="text-body font-body text-red-700">
                          This will permanently delete ALL GL accounts, transactions, reports, registers, and all related data. 
                          Everything will be gone and this action cannot be undone.
                        </span>
                      </div>
                    </div>
                    <p className="text-body font-body text-subtext-color font-semibold">
                      Type <strong>TRUNCATE ALL</strong> to confirm:
                    </p>
                  </div>
                </div>
              </div>
              
              {truncateError && (
                <div className="flex w-full items-start gap-3 rounded-md border border-solid border-red-200 bg-red-50 p-3">
                  <FeatherXCircle className="text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-body font-body text-red-900">{truncateError}</span>
                </div>
              )}

              {truncateSuccess && (
                <div className="flex w-full items-start gap-3 rounded-md border border-solid border-green-200 bg-green-50 p-3">
                  <FeatherCheckCircle className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-body font-body text-green-900">{truncateSuccess}</span>
                </div>
              )}

              <input
                type="text"
                value={truncateConfirmation}
                onChange={(e) => setTruncateConfirmation(e.target.value)}
                placeholder="Type TRUNCATE ALL to confirm"
                className="w-full px-4 py-2 border border-solid border-neutral-border rounded-md bg-default-background text-body font-body text-default-font"
                disabled={isTruncating}
              />
              
              <div className="flex w-full items-center justify-end gap-2">
                <Button
                  variant="neutral-secondary"
                  onClick={() => {
                    setShowTruncateModal(false);
                    setTruncateConfirmation("");
                    setTruncateError("");
                    setTruncateSuccess("");
                  }}
                  disabled={isTruncating}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive-primary"
                  icon={<FeatherTrash2 />}
                  onClick={handleTruncateAll}
                  disabled={truncateConfirmation !== "TRUNCATE ALL" || isTruncating}
                >
                  {isTruncating ? "Truncating..." : "Truncate All"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DefaultPageLayout>
  );
};